import { deferShellRender } from '../ui/shell_input_guard.mjs';
import { renderSystemSidebarCards } from '../sidebar/system_sidebar_cards.mjs';
import * as AppOptionsController from '../app/app_options_controller.js?v=20260904f';
import { S } from '../app/app_state.js?v=20260817c';
import {
  state,
  activeSystem,
  allCompanies,
  allSystems,
  categoriesForCompany,
  systemsForCategory,
  normalizeNavigationState,
  ensureSystemLoaded,
  isSystemLoaded,
  getSystem,
  getCompany,
  getCategory,
  getSystemRegistryRevision
} from './system_registry_core.js?v=20260904f';

const runtimeCallbacks = {
  refresh: null,
  refreshChromeOnly: null,
  clearGlobalSelection: null
};

function reportSystemBoundaryFailure(systemId, hookName, error) {
  try {
    console.warn(`[system-boundary] ${hookName} von ${systemId || '(unbekannt)'} ist fehlgeschlagen.`, error);
  } catch (_reportingError) {
    // Fehlerreporting darf die Systemgrenze nicht aufheben.
  }
}

function isolateUnexpectedThenable(system, hookName, value) {
  if (!value || (typeof value !== 'object' && typeof value !== 'function')) return false;
  let then;
  try {
    then = value.then;
  } catch (error) {
    reportSystemBoundaryFailure(system?.id, `${hookName}/Thenable`, error);
    return true;
  }
  if (typeof then !== 'function') return false;
  try {
    Promise.resolve(value).then(
      () => undefined,
      error => reportSystemBoundaryFailure(system?.id, `${hookName}/Thenable`, error)
    );
  } catch (error) {
    reportSystemBoundaryFailure(system?.id, `${hookName}/Thenable`, error);
  }
  return true;
}

// renderCompanies/renderCategories/renderSystems only depend on registry
// data (companies/categories/systems/their links, tracked via
// getSystemRegistryRevision()) and which company/category/system is
// currently active — never on live per-click UI/tool state. renderShell()
// runs on every single AppController.refresh() (i.e. on every click, drag,
// pan), so without this guard those three full innerHTML rebuilds of the
// header ran on every interaction regardless of whether anything they
// depend on had actually changed. renderToolbar() is intentionally NOT
// covered by this signature — its buttons reflect live per-system tool
// state (e.g. isToolbarActionActive), so it must keep re-rendering every
// refresh.
let lastRenderedHeaderSignature = null;

function requestRefresh(fit = false) {
  if (typeof runtimeCallbacks.refresh === 'function') runtimeCallbacks.refresh(Boolean(fit));
}

function requestChromeRefresh(reason = '') {
  if (typeof runtimeCallbacks.refreshChromeOnly === 'function') {
    runtimeCallbacks.refreshChromeOnly(reason);
  } else {
    requestRefresh(false);
  }
}

/**
 * Escaped Attributwerte fuer dynamisch gerenderte Header-Buttons.
 *
 * Zweck:
 * - haelt Logo-URLs und Alt-Texte als reine Registry-Daten
 * - verhindert fehlerhafte Attribute, ohne Produktlogik in den Core zu ziehen
 *
 * @param {*} value - Wert fuer ein HTML-Attribut.
 * @returns {string} Sicherer Attributwert.
 */
function escapeHeaderAttr(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Verwaltet Toolbar-, Seitenleisten- oder Werkzeuglogik fuer `buttonHtml`.
 *
 * Zweck:
 * - koppelt Benutzerwerkzeuge an die Systemoperationen
 * - aktualisiert UI-Zustand ohne die fachliche Modelllogik zu duplizieren
 * @param {*} button - Eingabewert fuer die fachliche Verarbeitung.
 * @param {*} system - Eingabewert fuer die fachliche Verarbeitung.
 * @returns {*} Ergebniswert fuer weitere Berechnung, Rendering oder UI-Synchronisation.
 */
function buttonHtml(button, system = activeSystem()) {
  const primary = button.primary ? ' primary' : '';
  const danger = button.danger ? ' danger' : '';
  const active = system?.isToolbarActionActive?.(button.action) ? ' is-active' : '';
  const selected = !active && system?.isToolbarActionSelected?.(button.action) ? ' is-selected' : '';
  const disabled = resolveToolbarControlOption(button, 'disabled', false) ? ' disabled' : '';
  return `<button type="button" id="${escapeHeaderAttr(button.id)}" class="btn${primary}${danger}${active}${selected}" data-action="${escapeHeaderAttr(button.action)}"${disabled} aria-pressed="${active ? 'true' : 'false'}">${escapeHeaderAttr(button.label)}</button>`;
}

function resolveToolbarControlOption(control, key, fallback = '') {
  const value = control?.[key];
  if (typeof value !== 'function') return value ?? fallback;
  try {
    return value();
  } catch (error) {
    console.warn(`KS Studio: System-Toolbarwert ${key} konnte nicht aufgeloest werden`, error);
    return fallback;
  }
}

function toolbarInputHtml(control) {
  const id = escapeHeaderAttr(control.id);
  const action = escapeHeaderAttr(control.action);
  const label = escapeHeaderAttr(control.label);
  const showLabel = resolveToolbarControlOption(control, 'showLabel', true) !== false;
  const inlineHint = resolveToolbarControlOption(control, 'inlineHint', false) === true;
  const inputType = control.inputType === 'text' ? 'text' : 'number';
  const value = escapeHeaderAttr(resolveToolbarControlOption(control, 'getValue', control.value ?? ''));
  const disabled = resolveToolbarControlOption(control, 'disabled', false) ? ' disabled' : '';
  const numberAttributes = inputType === 'number'
    ? [
        ['min', resolveToolbarControlOption(control, 'getMin', control.min)],
        ['max', resolveToolbarControlOption(control, 'getMax', control.max)],
        ['step', resolveToolbarControlOption(control, 'getStep', control.step)]
      ]
        .filter(([, option]) => option !== undefined && option !== null && option !== '')
        .map(([name, option]) => ` ${name}="${escapeHeaderAttr(option)}"`)
        .join('')
    : '';
  const unit = control.unit ? `<span class="system-toolbar-input-unit">${escapeHeaderAttr(control.unit)}</span>` : '';
  const hintValue = resolveToolbarControlOption(control, 'getHint', control.hint || '');
  const hint = hintValue
    ? `<span class="system-toolbar-input-hint">${escapeHeaderAttr(hintValue)}</span>`
    : '';
  const labelHtml = showLabel
    ? `<span class="system-toolbar-input-label">${label}</span>`
    : '';
  const inlineClass = inlineHint ? ' has-inline-hint' : '';
  return `<label class="system-toolbar-input-field${inlineClass}" for="${id}">${labelHtml}<span class="system-toolbar-input-row"><input id="${id}" type="${inputType}" data-toolbar-action="${action}" value="${value}"${numberAttributes}${disabled} aria-label="${label}">${unit}</span>${hint}</label>`;
}

function toolbarControlHtml(control, system = activeSystem()) {
  if (resolveToolbarControlOption(control, 'visible', true) === false) return '';
  if (control.controlType === 'input') return toolbarInputHtml(control);
  return buttonHtml(control, system);
}

function toolbarGroupLabel(groupId, buttons = []) {
  const explicit = buttons.find(button => button.groupLabel)?.groupLabel;
  if (explicit) return explicit;
  return groupId === 'default' ? 'Zeichnen' : 'Werkzeug';
}

/**
 * Beschreibt toolbar Html im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} buttons - Eingabewert für toolbar Html.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function toolbarHtml(buttons) {
  const system = activeSystem();
  const groups = [];
  const groupMap = new Map();

  for (const button of buttons) {
    const group = button.group || 'default';
    if (!groupMap.has(group)) {
      const entry = { type: 'group', id: group, buttons: [] };
      groupMap.set(group, entry);
      groups.push(entry);
    }
    groupMap.get(group).buttons.push(button);
  }

  return groups.map(entry => {
    const label = toolbarGroupLabel(entry.id, entry.buttons);
    return `<div class="command-card toolbar-card" data-toolbar-group="${escapeHeaderAttr(entry.id)}" data-toolbar-card="${escapeHeaderAttr(entry.id)}"><span class="command-card-title">${escapeHeaderAttr(label)}</span><div class="command-card-body">${entry.buttons.map(button => toolbarControlHtml(button, system)).join('')}</div></div>`;
  }).join('');
}

/**
 * Rendert die Firmenfilter in der linken Header-Spalte.
 *
 * Zweck:
 * - stellt registrierte Firmen als getrennte Kontexte dar
 * - laesst Core weiterhin nur Registry-Daten anzeigen, ohne Produktlogik zu kennen
 *
 * @sideEffects Schreibt die Firmenbuttons in den Header-DOM.
 */
function renderCompanies() {
  const host = document.getElementById('companySwitcher');
  if (!host) return;
  const available = allCompanies();
  if (!available.length && host.innerHTML) {
    console.warn('KS Studio: Firmenheader nicht geleert, weil keine Firmen registriert sind.', { state: { ...state } });
    return;
  }
  host.innerHTML = available.map(company => {
    const active = company.id === state.activeCompanyId ? ' active' : '';
    const logo = company.logoSrc
      ? `<img class="company-logo" src="${escapeHeaderAttr(company.logoSrc)}" alt="${escapeHeaderAttr(company.logoAlt || company.label)}">`
      : `<span class="company-label">${escapeHeaderAttr(company.label)}</span>`;
    // Firmenlogos bleiben klickbare Buttons; die Registry liefert nur Quelle und Alt-Text.
    return `<button type="button" class="company-button${active}" data-company="${escapeHeaderAttr(company.id)}" aria-label="${escapeHeaderAttr(company.label)}">${logo}</button>`;
  }).join('');
}

/**
 * Rendert render Categories im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function renderCategories() {
  const host = document.getElementById('categorySwitcher');
  if (!host) return;
  const available = categoriesForCompany(state.activeCompanyId);
  if (!available.length && host.innerHTML) {
    console.warn('KS Studio: Kategorieheader nicht geleert, weil keine Kategorien fuer die aktive Firma vorhanden sind.', { state: { ...state } });
    return;
  }
  host.innerHTML = available.map(category => {
    const active = category.id === state.activeCategoryId ? ' active' : '';
    return `<button type="button" class="header-button category-button${active}" data-category="${category.id}">${category.label}</button>`;
  }).join('');
}

/**
 * Rendert render Systems im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
function renderSystems() {
  const host = document.getElementById('systemSwitcher');
  if (!host) return;
  const available = systemsForCategory(state.activeCategoryId);
  if (!available.length && host.innerHTML) {
    console.warn('KS Studio: Systemheader nicht geleert, weil keine Systeme fuer die aktive Kategorie vorhanden sind.', { state: { ...state } });
    return;
  }
  host.innerHTML = available.map(system => {
    const active = system.id === state.activeSystemId ? ' active' : '';
    const icon = system.icon
      ? `<img src="${escapeHeaderAttr(system.icon)}" alt="" loading="lazy" decoding="async" onerror="this.style.display='none'">`
      : '';
    return `<button type="button" class="header-button system-button${active}" data-system="${escapeHeaderAttr(system.id)}"><span class="system-button-icon">${icon}</span><span class="system-button-divider" aria-hidden="true"></span><span class="system-button-label">${escapeHeaderAttr(system.label)}</span></button>`;
  }).join('');
}

/**
 * Aktualisiert systembezogene Aktivierungsdefaults ohne spaetere Nutzereingaben zu ueberschreiben.
 *
 * Zweck:
 * - setzt produktbezogene Startwerte nur einmal beim ersten Wechsel in ein System
 * - danach bleibt der globale Raster-/Snap-State ein Benutzer-State
 */
function applyActiveSystemDefaults(system = activeSystem(), context = {}) {
  if (typeof system?.applyActivationDefaults !== 'function') return false;
  return system.applyActivationDefaults({
    system,
    state,
    project: S.project,
    ui: S.ui,
    selection: S.selection,
    ...context
  }) === true;
}

function loadSystemAfterNavigation(system) {
  if (!system || isSystemLoaded(system.id)) return false;
  ensureSystemLoaded(system.id)
    .then(loadedSystem => {
      if (!loadedSystem || state.activeSystemId !== system.id) return;
      applyActiveSystemDefaults(loadedSystem, { reason: 'lazy-system-loaded' });
      renderShell();
      AppOptionsController.persistAppOptions();
      requestRefresh(false);
    })
    .catch(error => {
      console.error(`System konnte nicht geladen werden: ${system.id}`, error);
      requestChromeRefresh(`lazy-system-load-failed:${system.id}`);
    });
  return true;
}

export function configureRuntime(callbacks = {}) {
  if (typeof callbacks.refresh === 'function') runtimeCallbacks.refresh = callbacks.refresh;
  if (typeof callbacks.refreshChromeOnly === 'function') runtimeCallbacks.refreshChromeOnly = callbacks.refreshChromeOnly;
  if (typeof callbacks.clearGlobalSelection === 'function') runtimeCallbacks.clearGlobalSelection = callbacks.clearGlobalSelection;
}

/**
 * Rendert render Shell im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
export function renderShell() {
  normalizeNavigationState();
  const companiesAll = allCompanies();
  const categoriesForActive = categoriesForCompany(state.activeCompanyId);
  const systemsForActive = systemsForCategory(state.activeCategoryId);
  if (!companiesAll.length || !categoriesForActive.length || !systemsForActive.length) {
    console.warn('KS Studio: Header-Rendering übersprungen, weil die SystemRegistry leer ist.', {
      companies: companiesAll.length,
      categories: categoriesForActive.length,
      systems: systemsForActive.length,
      state: { ...state }
    });
    return;
  }
  applyActiveSystemDefaults();
  const headerSignature = `${getSystemRegistryRevision()}|${state.activeCompanyId}|${state.activeCategoryId}|${state.activeSystemId}`;
  if (headerSignature !== lastRenderedHeaderSignature) {
    lastRenderedHeaderSignature = headerSignature;
    renderCompanies();
    renderCategories();
    renderSystems();
  }
  renderToolbar();
}

/**
 * Rendert render Toolbar im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Schreibt in DOM, SVG, Canvas oder Three.js-Strukturen und beeinflusst die sichtbare Darstellung.
 */
export function renderToolbar() {
  if (deferShellRender('system-toolbar', renderToolbar)) return;
  const host = document.getElementById('systemToolbar');
  if (!host) return;
  const system = activeSystem();
  host.innerHTML = system ? toolbarHtml(system.toolbarButtons) : '';
}

/**
 * Beschreibt activate System im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} options - Optionale Steuerwerte, die Defaults oder Verhalten überlagern.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function activateSystem(systemId, options = {}) {
  const system = getSystem(systemId);
  if (!system) return false;
  // Ein System kann per Header-Konfiguration in mehreren Kategorien
  // erscheinen; bleibt es in der aktuell aktiven Kategorie sichtbar, wird
  // diese beibehalten statt auf `system.categoryId` (die urspruengliche,
  // hart codierte Einzelzuordnung) zurueckzuspringen.
  if (!systemsForCategory(state.activeCategoryId).some(entry => entry.id === system.id)) {
    state.activeCompanyId = system.companyId || state.activeCompanyId || 'default';
    state.activeCategoryId = system.categoryId;
  }
  state.activeSystemId = system.id;
  applyActiveSystemDefaults(system);
  renderShell();
  AppOptionsController.persistAppOptions();
  loadSystemAfterNavigation(system);
  if (options.refreshView) {
    requestRefresh(Boolean(options.fit));
  }
  return true;
}

/**
 * Aktiviert eine Firma und waehlt deren erste Kategorie sowie erstes Produkt.
 *
 * Zweck:
 * - trennt die Navigation sauber zwischen registrierten Firmen
 * - vermeidet firmenspezifische Sonderlogik ausserhalb der Registry
 *
 * @param {string} companyId - Ziel-Firma.
 * @param {Object} options - Optionales Refresh-/Fit-Verhalten analog `activateSystem`.
 * @returns {boolean} `true`, wenn die Firma existiert und aktiviert wurde.
 */
export function activateCompany(companyId, options = {}) {
  if (!getCompany(companyId)) return false;
  state.activeCompanyId = companyId;
  const firstCategory = categoriesForCompany(companyId)[0] || null;
  state.activeCategoryId = firstCategory?.id || null;
  const firstSystem = systemsForCategory(state.activeCategoryId)[0] || null;
  state.activeSystemId = firstSystem?.id || null;
  applyActiveSystemDefaults();
  renderShell();
  AppOptionsController.persistAppOptions();
  loadSystemAfterNavigation(firstSystem);
  if (options.refreshView) {
    requestRefresh(Boolean(options.fit));
  }
  return true;
}

/**
 * Aktiviert eine Systemgruppe und waehlt das erste passende System.
 *
 * Zweck:
 * - bildet die zweite Filterebene von Header und Einstellungen ab
 * - haelt Systemgruppenwechsel generisch und firmenunabhaengig
 *
 * @param {string} categoryId - Ziel-Systemgruppe bzw. Kategorie.
 * @param {Object} options - Optionales Refresh-/Fit-Verhalten analog `activateSystem`.
 * @returns {boolean} `true`, wenn eine passende Kategorie aktiviert wurde.
 */
export function activateCategory(categoryId, options = {}) {
  const category = getCategory(categoryId);
  if (!category) return false;
  // Analog zu activateSystem: eine Kategorie kann per Header-Konfiguration
  // mehreren Firmen zugeordnet sein; bleibt sie unter der aktiven Firma
  // sichtbar, wird diese beibehalten.
  if (!categoriesForCompany(state.activeCompanyId).some(entry => entry.id === category.id)) {
    state.activeCompanyId = category.companyId || state.activeCompanyId || 'default';
  }
  state.activeCategoryId = category.id;
  const firstSystem = systemsForCategory(state.activeCategoryId)[0] || null;
  state.activeSystemId = firstSystem?.id || null;
  applyActiveSystemDefaults(firstSystem);
  renderShell();
  AppOptionsController.persistAppOptions();
  loadSystemAfterNavigation(firstSystem);
  if (options.refreshView) {
    requestRefresh(Boolean(options.fit));
  }
  return true;
}

export function clearSelectionForNavigation(currentId, nextId) {
  if (String(currentId || '') === String(nextId || '')) return false;
  if (typeof runtimeCallbacks.clearGlobalSelection === 'function') {
    return runtimeCallbacks.clearGlobalSelection();
  }
  return clearAllSelections();
}

/**
 * Beschreibt bind Navigation im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * Seiteneffekte: Beachtet fachliche Hilfslogik im jeweiligen Modul; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
export function bindNavigation() {
  document.getElementById('companySwitcher')?.addEventListener('click', event => {
    const button = event.target.closest('[data-company]');
    if (!button) return;
    clearSelectionForNavigation(state.activeCompanyId, button.dataset.company);
    activateCompany(button.dataset.company);
    requestChromeRefresh('company-switch');
  });

  document.getElementById('categorySwitcher')?.addEventListener('click', event => {
    const button = event.target.closest('[data-category]');
    if (!button) return;
    clearSelectionForNavigation(state.activeCategoryId, button.dataset.category);
    activateCategory(button.dataset.category);
    requestChromeRefresh('category-switch');
  });

  document.getElementById('systemSwitcher')?.addEventListener('click', event => {
    const button = event.target.closest('[data-system]');
    if (!button) return;
    clearSelectionForNavigation(state.activeSystemId, button.dataset.system);
    activateSystem(button.dataset.system);
    requestChromeRefresh('system-switch');
  });
}

/**
 * Verarbeitet handle Toolbar Action im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} action - Eingabewert für handle Toolbar Action.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handleToolbarAction(action, payload) {
  const system = activeSystem();
  if (!system?.handleToolbarAction) return false;
  try {
    const result = system.handleToolbarAction(action, payload);
    if (isolateUnexpectedThenable(system, 'Toolbar-Hook', result)) return false;
    return result === true;
  } catch (error) {
    reportSystemBoundaryFailure(system.id, 'Toolbar-Hook', error);
    return false;
  }
}

/**
 * Verarbeitet handle Tool Action im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Toolbar-, Werkzeug- und Bedienzustände, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @param {*} action - Eingabewert für handle Tool Action.
 * @param {*} payload - Eingabewert für handle Tool Action.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function handleToolAction(systemId, action, payload = null) {
  const system = getSystem(systemId);
  if (!system?.handleToolAction) return false;
  try {
    const result = system.handleToolAction(action, payload);
    if (isolateUnexpectedThenable(system, 'Tool-Hook', result)) return false;
    return result === true;
  } catch (error) {
    reportSystemBoundaryFailure(system.id, 'Tool-Hook', error);
    return false;
  }
}

/**
 * Rendert render Active UI im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Darstellung im Plan, Layer-Aufbau oder grafische Markierung, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function renderActiveUI() {
  if (deferShellRender('system-ui', renderActiveUI)) return true;
  const cardsRendered = renderSystemSidebarCards();
  const system = activeSystem();
  if (!system?.renderUI) return cardsRendered;
  system.renderUI();
  return true;
}

export function fitActiveCamera(context = {}) {
  const system = activeSystem();
  if (typeof system?.fitCamera !== 'function') return false;
  return system.fitCamera({ system, ...context }) !== false;
}

/**
 * Entfernt clear Active Selection im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function clearActiveSelection() {
  const system = activeSystem();
  if (!system?.clearSelection) return false;
  try {
    const result = system.clearSelection();
    if (isolateUnexpectedThenable(system, 'Hook clearSelection', result)) return false;
    return true;
  } catch (error) {
    reportSystemBoundaryFailure(system.id, 'Hook clearSelection', error);
    return false;
  }
}

/**
 * Entfernt clear All Selections im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt Auswahl, Trefferprüfung und Synchronisierung der UI-Selektion, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function clearAllSelections() {
  let cleared = false;
  for (const system of allSystems()) {
    if (!system.clearSelection) continue;
    try {
      const result = system.clearSelection();
      if (isolateUnexpectedThenable(system, 'Hook clearSelection', result)) continue;
      cleared = true;
    } catch (error) {
      reportSystemBoundaryFailure(system.id, 'Hook clearSelection', error);
    }
  }
  return cleared;
}

export function clearSelectionsExcept(systemId) {
  let cleared = false;
  for (const system of allSystems()) {
    if (system.id === systemId || !system.clearSelection) continue;
    try {
      const result = system.clearSelection();
      if (isolateUnexpectedThenable(system, 'Hook clearSelection', result)) continue;
      cleared = true;
    } catch (error) {
      reportSystemBoundaryFailure(system.id, 'Hook clearSelection', error);
    }
  }
  return cleared;
}
